import { lazy, useMemo } from 'react';
import type { SurrendersParams } from 'src/apis/monolith/types';
import type { PlatformSettings } from 'src/apis/monolith/use-platform-settings';
import { useOptionalPlatformSettings } from 'src/apis/monolith/use-platform-settings';
import { useSurrenders } from 'src/apis/monolith/use-surrenders';
import { useSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { PageSpinner } from 'src/components/spinner-container';
import { useSurrendersPageParams } from 'src/pages/reporting/surrenders/overview/use-page-params';

const Page = lazy(() => import('./page'));

export const LegacySurrendersOverview = () => {
  const platformSettings = useOptionalPlatformSettings();

  if (platformSettings?.loaded === false) return <PageSpinner />;

  return (
    <LoadData platformSettings={platformSettings?.response?.data ?? null} />
  );
};

type LoadDataProps = {
  platformSettings: PlatformSettings | null;
};

const LoadData = ({ platformSettings }: LoadDataProps) => {
  const pageParams = useSurrendersPageParams({ platformSettings });
  const surrenderRoot = useSurrenderRoot();

  const params = useMemo(
    () =>
      ({
        start: pageParams.value.start,
        pageSize: pageParams.value.pageSize,
        sortColumn: pageParams.value.sortColumn,
        sortAscending: pageParams.value.sortAscending === 'asc',
        favoritesOnly: pageParams.value.favoritesOnly,
        dealIds: pageParams.value.dealId,
        runtimeFrom: pageParams.value.runtimeFrom,
        runtimeTo: pageParams.value.runtimeTo,
        direction: pageParams.value.direction,
        tso: pageParams.value.tso.map((tso) => tso.value),
        networkPointName: pageParams.value.networkPointName.map(
          (item) => item.value
        ),
        'bookedCapacity-min': pageParams.value['bookedCapacity-min'],
        'bookedCapacity-max': pageParams.value['bookedCapacity-max'],
        amountSetting: pageParams.value.amountSetting,
        costSetting: pageParams.value.costSetting,
      }) satisfies SurrendersParams,
    [pageParams.value]
  );

  const surrenders = useSurrenders({ params });

  if (!surrenderRoot.response || !surrenders.response) return <PageSpinner />;

  return (
    <Page
      pageParams={pageParams}
      surrendersParams={params}
      surrenders={surrenders}
      isSurrenderForAllEnabled={
        !!surrenderRoot.response.data._links.isSurrenderForAllEnabled
      }
    />
  );
};
